'use client';

import { QuestionCircle } from '@doist/icons/brand';
import { Box, Button, Image } from '@doist/marketist';
import { useTranslation } from 'app/i18n/client';
import cn from 'clsx';
import { useLocale } from 'next-intl';
import { useArchitecture } from 'services/hooks/use-architecture';
import css from './common.module.css';
import type { Platform, PlatformType } from '../../services/valid-platforms';
function LinuxActions() {
  const locale = useLocale();
  const {
    t
  } = useTranslation(locale, 'downloads');
  return <Box className={cn(css.buttonContainer, 'mbs-24')} data-sentry-element="Box" data-sentry-component="LinuxActions" data-sentry-source-file="actions.tsx">
            <Box className={css.buttonRow} data-sentry-element="Box" data-sentry-source-file="actions.tsx">
                <Button href="https://todoist.com/linux_app/snap" data-sentry-element="Button" data-sentry-source-file="actions.tsx">
                    {t('platform.linux.snapStoreButton')}
                </Button>
                <Button variant="secondary" href="https://todoist.com/linux_app/appimage" data-sentry-element="Button" data-sentry-source-file="actions.tsx">
                    {t('platform.linux.appImageButton')}
                </Button>
            </Box>
            <Button size="small" variant="tertiary" startIcon={QuestionCircle} href="/help/articles/install-todoist-for-linux-KgKaJa6H#h_01HC1J8QVXAT8XVV99V521SHD4" data-sentry-element="Button" data-sentry-source-file="actions.tsx">
                {t('platform.linux.subtext')}
            </Button>
        </Box>;
}
function MacOSActions() {
  const locale = useLocale();
  const {
    t
  } = useTranslation(locale, 'downloads');
  const architecture = useArchitecture();
  return <Box className={cn(css.buttonContainer, 'mbs-24')} data-sentry-element="Box" data-sentry-component="MacOSActions" data-sentry-source-file="actions.tsx">
            <Box className={css.buttonRow} data-sentry-element="Box" data-sentry-source-file="actions.tsx">
                {architecture === 'arm' ? <>
                        <Button href="https://todoist.com/mac_app?arch=arm">
                            {t('platform.mac.siliconButton')}
                        </Button>
                        <Button variant="secondary" href="https://todoist.com/mac_app?arch=x64">
                            {t('platform.mac.intelButton')}
                        </Button>
                    </> : <>
                        <Button href="https://todoist.com/mac_app?arch=x64">
                            {t('platform.mac.intelButton')}
                        </Button>
                        <Button variant="secondary" href="https://todoist.com/mac_app?arch=arm">
                            {t('platform.mac.siliconButton')}
                        </Button>
                    </>}
            </Box>
            <Button size="small" variant="tertiary" startIcon={QuestionCircle} href="/help/articles/system-requirements-for-todoist-Bqx0EW#h_01HEVG3V82Z0CKMWG9PNPT64BS" data-sentry-element="Button" data-sentry-source-file="actions.tsx">
                {t('platform.mac.subtext')}
            </Button>
        </Box>;
}
function WindowsActions() {
  const locale = useLocale();
  const {
    t
  } = useTranslation(locale, 'downloads');
  return <Box className={cn(css.buttonContainer, 'mbs-24')} data-sentry-element="Box" data-sentry-component="WindowsActions" data-sentry-source-file="actions.tsx">
            <Button href="https://todoist.com/windows_app" data-sentry-element="Button" data-sentry-source-file="actions.tsx">
                {t('platform.windows.windowsButton')}
            </Button>
        </Box>;
}
function IosActions({
  type
}: {
  type?: PlatformType;
}) {
  const locale = useLocale();
  const {
    t
  } = useTranslation(locale, 'downloads');
  return <Box className={cn(css.mobileContainer, 'mbs-24')} data-sentry-element="Box" data-sentry-component="IosActions" data-sentry-source-file="actions.tsx">
            <a target="_blank" href="https://geo.itunes.apple.com/us/app/todoist-to-do-list-task-manager/id572688855" rel="noreferrer">
                <Image width={135} height={40} src="/static/downloads/buttons/app-store.svg" alt={t('platform.iPhone.appStoreButton')} data-sentry-element="Image" data-sentry-source-file="actions.tsx" />
            </a>
            {type === 'desktop' && <Image width={72} height={70} src="/static/downloads/app-store-qr-code.png" />}
        </Box>;
}
function AndroidActions({
  type
}: {
  type?: PlatformType;
}) {
  const locale = useLocale();
  const {
    t
  } = useTranslation(locale, 'downloads');
  return <Box className={cn(css.mobileContainer, 'mbs-24')} data-sentry-element="Box" data-sentry-component="AndroidActions" data-sentry-source-file="actions.tsx">
            <a target="_blank" href="https://play.google.com/store/apps/details?id=com.todoist" rel="noreferrer">
                <Image width={135} height={40} src="/static/downloads/buttons/play-store.svg" alt={t('platform.android.googlePlayButton')} data-sentry-element="Image" data-sentry-source-file="actions.tsx" />
            </a>
            {type === 'desktop' && <Image width={72} height={70} src="/static/downloads/google-play-qr-code.png" />}
        </Box>;
}
type ActionsProps = {
  platform: Platform;
  type?: PlatformType;
};
function Actions({
  platform,
  type
}: ActionsProps) {
  switch (platform) {
    case 'linux':
      return <LinuxActions />;
    case 'mac':
      return <MacOSActions />;
    case 'windows':
      return <WindowsActions />;
    case 'iPhone':
      return <IosActions type={type} />;
    case 'iPad':
      return <IosActions type={type} />;
    case 'appleWatch':
      return <IosActions />;
    case 'android':
      return <AndroidActions type={type} />;
    case 'wearOS':
      return <AndroidActions />;
    default:
      return null;
  }
}
export { Actions };